import {
  Bell,
  ChevronRight,
  Menu,
  Palette,
  Scale,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { MAIL_LEGAL_LINKS, mailLegalExternalLinkProps } from "@/lib/legal/mailLegalLinks";
import { getMailNotificationSoundOption } from "@/lib/mail/mailNotificationSoundPrefs";
import { MailHeaderIconButton } from "./MailHeaderIconButton";
import { MailNotificationSoundPanel } from "./MailNotificationSoundPanel";
import { MailWebPushSettings } from "./MailWebPushSettings";
import { MailThemePanel } from "./MailThemePanel";
import { cn } from "@/lib/cn";

const LEGAL_ITEMS = [
  { href: MAIL_LEGAL_LINKS.home, label: "À propos" },
  { href: MAIL_LEGAL_LINKS.privacy, label: "Confidentialité" },
  { href: MAIL_LEGAL_LINKS.terms, label: "Conditions" },
  { href: MAIL_LEGAL_LINKS.mentions, label: "Mentions légales" },
] as const;

type Panel = "main" | "theme" | "sound" | "legal";

const PANEL_TITLE: Record<Exclude<Panel, "main">, string> = {
  theme: "Thème et couleur",
  sound: "Notifications",
  legal: "Informations légales",
};

type Props = {
  /** Position du panneau (top = drawer mobile bas d’écran). */
  menuPlacement?: "bottom" | "top";
};

/** Menu burger header — thème, notifs (son + système + QR), légal, déconnexion. */
export function MailShellOverflowMenu({ menuPlacement = "bottom" }: Props) {
  const [open, setOpen] = useState(false);
  const [panel, setPanel] = useState<Panel>("main");
  const [soundLabel, setSoundLabel] = useState(
    () => getMailNotificationSoundOption().label
  );
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
        setPanel("main");
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (panel !== "main") {
          setPanel("main");
          return;
        }
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, panel]);

  function closeMenu() {
    setOpen(false);
    setPanel("main");
  }

  function openMenu() {
    setSoundLabel(getMailNotificationSoundOption().label);
    setOpen((value) => {
      if (value) setPanel("main");
      return !value;
    });
  }

  return (
    <div ref={rootRef} className="relative">
      <MailHeaderIconButton
        label="Menu"
        onClick={openMenu}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Menu className="h-4 w-4 shrink-0" aria-hidden />
      </MailHeaderIconButton>

      {open ? (
        <div
          className={cn(
            "mail-shell-overflow-menu absolute right-0 z-[121] w-[17.5rem] overflow-hidden rounded-2xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface)] shadow-lg",
            menuPlacement === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5"
          )}
          role="menu"
          aria-label="Menu Aurélia Mail"
        >
          {panel === "main" ? (
            <div className="py-1.5">
              <button
                type="button"
                role="menuitem"
                className="mail-shell-overflow-row"
                onClick={() => setPanel("theme")}
              >
                <Palette className="mail-shell-overflow-row__icon" aria-hidden />
                <span className="mail-shell-overflow-row__label">
                  <span className="mail-shell-overflow-row__title">Thème et couleur</span>
                  <span className="mail-shell-overflow-row__hint">Apparence de l’app</span>
                </span>
                <ChevronRight className="mail-shell-overflow-row__chevron" aria-hidden />
              </button>

              <button
                type="button"
                role="menuitem"
                className="mail-shell-overflow-row"
                onClick={() => setPanel("sound")}
              >
                <Bell className="mail-shell-overflow-row__icon" aria-hidden />
                <span className="mail-shell-overflow-row__label">
                  <span className="mail-shell-overflow-row__title">Notifications</span>
                  <span className="mail-shell-overflow-row__hint">
                    {soundLabel} · système · QR
                  </span>
                </span>
                <ChevronRight className="mail-shell-overflow-row__chevron" aria-hidden />
              </button>

              <div className="my-1.5 border-t border-[var(--mail-border-soft)]" role="separator" />

              <button
                type="button"
                role="menuitem"
                className="mail-shell-overflow-row"
                onClick={() => setPanel("legal")}
              >
                <Scale className="mail-shell-overflow-row__icon" aria-hidden />
                <span className="mail-shell-overflow-row__label">
                  <span className="mail-shell-overflow-row__title">Informations légales</span>
                  <span className="mail-shell-overflow-row__hint">À propos, confidentialité…</span>
                </span>
                <ChevronRight className="mail-shell-overflow-row__chevron" aria-hidden />
              </button>
            </div>
          ) : (
            <div className="max-h-[min(70vh,34rem)] overflow-y-auto p-3">
              <button
                type="button"
                className="mb-2.5 flex items-center gap-1 text-left text-[11px] font-medium text-[var(--mail-muted)] transition hover:text-[var(--mail-text)]"
                onClick={() => setPanel("main")}
              >
                ← Retour
                <span className="text-[var(--mail-text)]">· {PANEL_TITLE[panel]}</span>
              </button>

              {panel === "theme" ? (
                <MailThemePanel compact onClose={closeMenu} />
              ) : null}

              {panel === "sound" ? (
                <div className="space-y-3">
                  <MailWebPushSettings />
                  <div className="border-t border-[var(--mail-border-soft)] pt-2.5">
                    <p className="mb-1.5 px-0.5 text-sm font-medium text-[var(--mail-text)]">
                      Son des notifications
                    </p>
                    <MailNotificationSoundPanel
                      onPick={() => setSoundLabel(getMailNotificationSoundOption().label)}
                    />
                  </div>
                </div>
              ) : null}

              {panel === "legal" ? (
                <div className="space-y-0.5">
                  {LEGAL_ITEMS.map((item) => (
                    <a
                      key={item.href}
                      {...mailLegalExternalLinkProps(item.href)}
                      role="menuitem"
                      className="flex items-center rounded-lg px-2.5 py-2 text-sm text-[var(--mail-text)] transition hover:bg-[var(--mail-surface-hover)]"
                      onClick={closeMenu}
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
